<template>
  <v-container fluid>
    <h3 class="text-primary">Classification</h3>
    <v-row dense>
      <v-col cols="6">
        <v-select
          label="Classification"
          :items="classifications"
          :model-value="intakeStore.pageManager?.currentPage?.documentType"
          :disabled="!props.editable"
          @update:model-value="onDocumentTypeChange" />
      </v-col>
      <v-col v-if="showDueDate" cols="3">
        <text-date-input
          :max-date="null"
          label="Due Date"
          :model-value="intakeStore.pageManager?.currentPage?.dueDate"
          :disabled="!props.editable"
          @update:model-value="onDocumentDueDateChanged" />
      </v-col>
    </v-row>
    <v-row dense>
      <v-col v-if="props.editable" cols="3">
        <v-btn-lh class="mt-3" @click="onDocumentClassifyEntirely">Classify Entire Document</v-btn-lh>
      </v-col>
      <v-col v-if="props.showSplitButton" cols="3">
        <v-btn-lh class="mt-3" @click="onSplitDocumentManually">Split Document Manually</v-btn-lh>
      </v-col>
    </v-row>
  </v-container>
  <v-dialog v-model="showSplitDocumentManually" max-width="95%">
    <DocumentSplit :on-close="() => (showSplitDocumentManually = false)" />
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useIntakeStore } from "@/stores/intakeStore";
import { useRequirementsStore } from "@/stores";
import DocumentSplit from "./DocumentSplit.vue";

const props = defineProps({
  editable: {
    required: false,
    type: Boolean,
    default: false,
  },
  showSplitButton: {
    required: false,
    type: Boolean,
    default: false,
  },
});
const isDirty = ref(false);
const showSplitDocumentManually = ref(false);

const intakeStore = useIntakeStore();
const requirementsStore = useRequirementsStore();

const classifications = computed(() => {
  return requirementsStore.clientConfig?.intake.documentPageClassifications ?? [];
});

const showDueDate = computed(() => {
  return requirementsStore.clientConfig?.intake.showClassificationDueDate ?? false;
});

const onDocumentTypeChange = (value) => {
  if (intakeStore.pageManager?.currentPage) {
    intakeStore.pageManager.currentPage.updateAttribute("documentType", value);
    intakeStore.pageManager.currentPage.debouncedSaveUpdates();
    isDirty.value = true;
  }
};

const onDocumentDueDateChanged = (value) => {
  if (intakeStore.pageManager?.currentPage) {
    const currentClassification = intakeStore.pageManager?.currentPage?.documentType;
    intakeStore.pageManager?.pages?.forEach((page) => {
      if (page.documentType === currentClassification) {
        page.updateAttribute("dueDate", value);
        page.debouncedSaveUpdates();
      }
    });
    isDirty.value = true;
  }
};

const onDocumentClassifyEntirely = () => {
  const selectedClassification = intakeStore.pageManager?.currentPage?.documentType;
  const dueDate = intakeStore.pageManager?.currentPage?.dueDate;
  if (selectedClassification || dueDate) {
    intakeStore.pageManager?.pages?.forEach((page) => {
      if (page.documentType !== selectedClassification && selectedClassification) {
        page.updateAttribute("documentType", selectedClassification);
      }
      if (page.dueDate !== dueDate && dueDate) {
        page.updateAttribute("dueDate", dueDate);
      }
      page.debouncedSaveUpdates();
    });
    isDirty.value = true;
  }
};

const onSplitDocumentManually = () => {
  showSplitDocumentManually.value = true;
};

watch(
  () => intakeStore.pageManager?.currentPage,
  (newVal, oldVal) => {
    if (oldVal) {
      // If the user has edited the descriptor, save it before the current page is changed
      if (isDirty.value) {
        oldVal.saveUpdates();
        isDirty.value = false;
      }
    }
  }
);
</script>

<style scoped></style>
